<template>
  <div
    ref="editable-instance"
    class="editable-instance"
    :class="{
      'editable-instance--editing': isBeingEdited,
      'editable-instance--modal': isModal,
    }"
    @click.self="toogleEdition()"
    @mousemove="toggleButton"
    @mouseleave="showButton = false"
  >
    <button
      v-if="$route.name === 'editor' && !isViewerUser"
      v-show="showButton"
      class="btn btn-primary btn-icon"
      :class="{
        'btn--top': showButton === 'top',
        'btn--bottom': showButton === 'bottom',
      }"
      @click="togglePresetListDisplay({ instanceId, position: showButton })"
    >
      <IconPlus size="xs" />
      <span>Adicionar módulo</span>
    </button>
    <div
      v-if="!instanceId.includes('main') && !isModal && !isViewerUser"
      class="editable-instance__actions bg-blue-primary"
      :class="{ 'editable-instance__actions--page': !isModal }"
    >
      <button
        title="Mover para cima"
        class="no-border bg-blue-primary text-white no-outline"
        :disabled="isFirstInstance"
        @click.stop="moveInstanceUp()"
      >
        <IconArrow direction="up" />
      </button>
      <button
        title="Mover para baixo"
        class="no-border bg-blue-primary text-white no-outline"
        :disabled="isLastInstance"
        @click.stop="moveInstanceDown()"
      >
        <IconArrow direction="down" />
      </button>
      <button
        v-if="isInstanceEnabled"
        title="Duplicar módulo"
        class="no-border bg-blue-primary text-white no-outline"
        @click.stop="setVisibilityCloneDropdown()"
      >
        <IconCopy />
      </button>
      <button
        title="Remover módulo"
        class="no-border bg-blue-primary text-white no-outline"
        @click.stop="removeInstance()"
      >
        <IconDelete />
      </button>
      <div
        v-show="enableCloneDropdown"
        class="clone-dropdown"
        @mouseleave="!setVisibilityCloneDropdown(), !setVisibilityPageList()"
      >
        <button
          v-show="!isUniqueInstance"
          class="instance-properties__tab no-outline no-border bg-transparent"
          @click.stop="cloneInstance()"
        >
          Duplicar nessa mesma página
        </button>
        <hr />
        <button
          :class="{ 'show-page-list': enablePageList }"
          class="instance-properties__tab no-outline no-border bg-transparent"
          @click.stop="setVisibilityPageList()"
        >
          Copiar para outra página
          <IconArrow :direction="enablePageList ? 'up' : 'down'" />
        </button>
        <div
          v-show="enablePageList"
          class="no-select search"
        >
          <input
            v-model.trim="searchTerm"
            class="form-control search__field"
            type="text"
            placeholder="Buscar página"
            @click.stop
          />
          <IconSearch size="sm" />
          <div class="list-pages">
            <ul>
              <li
                v-for="(page, index) in mathedPagesLimit(3)"
                :key="index"
                @click.stop="cloneInstanceToAnotherPage(page)"
              >
                {{ page.title }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <button
        v-if="showVisibilityButton"
        class="visibility"
        @click="goToVisibility"
      >
        <IconEyeClosed />
        {{ visibilityButtonText }}
      </button>
    </div>
    <div
      v-if="!instanceId.includes('main') && isModal && !isViewerUser"
      class="editable-instance__actions bg-blue-primary"
      :class="{ 'editable-instance__actions--modal': isModal }"
    >
      <button
        v-if="showVisibilityButton"
        class="visibility"
        @click="goToVisibility"
      >
        <IconEyeClosed />
        {{ visibilityButtonText }}
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import slugify from 'slugify';

import * as Modules from '@/modules';
import ModalModules from '@/modules/modals';
import presets from '@/store/dicts/presets';
import isPresetEnabled from '@/store/helpers/isPresetEnabled';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconCopy from '@/components/icons/IconCopy.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import IconEyeClosed from '@/components/icons/IconEyeClosed.vue';
import getPropDefaults from '@/helpers/getPropDefaults';
import isObject from '@/helpers/isObject';
import Toast from '@/helpers/Toast';
import extendMetadata from '@/helpers/extendMetadata';
import getMetadataFields from '@/helpers/getMetadataFields';

export default {
  name: 'EditableInstance',
  components: {
    IconArrow,
    IconCopy,
    IconDelete,
    IconPlus,
    IconSearch,
    IconEyeClosed,
  },
  props: {
    instanceId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      Toast: null,
      child: null,
      searchTerm: '',
      enablePageList: false,
      enableCloneDropdown: false,
      showButton: false,
    };
  },
  computed: {
    moduleList() {
      return this.container.type === 'modals' ? ModalModules : Modules;
    },
    pages() {
      return this.pagesArray()
        .filter(({ id }) => !id.includes(this.$store.state.activePageId))
        .reverse();
    },
    pagesList() {
      return this.searchTerm !== '' ? this.mathedPages : this.pages;
    },
    mathedPages() {
      return this.pages.filter((pages) => this.findPage(pages));
    },
    instance() {
      return this.$store.getters.instance(this.instanceId);
    },
    metadata() {
      return extendMetadata(this.moduleList[this.instance.module].metadata);
    },
    metaDataKeyValue() {
      const allFields = getMetadataFields(this.metadata);
      const metaDataKeyValue = {};

      allFields.forEach(({ name }) => {
        metaDataKeyValue[name] = this.instance.data[name];
      });

      return metaDataKeyValue;
    },
    isModal() {
      return this.container.type === 'modals';
    },
    isUniqueInstance() {
      return presets.some(
        (preset) => preset.module === this.instance.module && preset.unique,
      );
    },
    isInstanceEnabled() {
      return presets.some(
        (preset) =>
          preset.module === this.instance.module &&
          isPresetEnabled(preset, this.$store.state, this.$store.getters),
      );
    },
    isBeingEdited() {
      return this.instanceId === this.$store.state.activeInstance;
    },
    isFirstInstance() {
      return this.$store.getters.isFirstInstance(this.instanceId);
    },
    isLastInstance() {
      return this.$store.getters.isLastInstance(this.instanceId);
    },
    showVisibilityButton() {
      // botão não deve aparecer caso o módulo não tenha a prop showOnDesk (não use BaseModule/BaseModal) ou esteja ligado em ambos desktop e mobile
      const { showOnDesk, showOnMobile } = this.metaDataKeyValue;

      return showOnDesk !== undefined && (!showOnDesk || !showOnMobile);
    },
    visibilityButtonText() {
      const { showOnDesk, showOnMobile } = this.metaDataKeyValue;

      if (!showOnDesk && !showOnMobile) {
        return 'Módulo ocultado';
      }

      return !showOnDesk ? 'Oculto no desktop' : 'Oculto no mobile';
    },
    ...mapGetters([
      'containerSettings',
      'globalData',
      'pagesArray',
      'container',
      'isViewerUser',
    ]),
  },
  created() {
    const propDefaults = getPropDefaults(this.moduleList[this.instance.module]);
    const presetDataKeys = Object.keys(this.instance.data);

    // We need to watch *every* module prop, not just the ones declared in preset,
    // so we merge preset data with module defaults
    Object.keys(propDefaults).forEach((key) => {
      if (
        !presetDataKeys.includes(key) &&
        Object.keys(this.metaDataKeyValue).includes(key)
      ) {
        Vue.set(this.instance.data, key, propDefaults[key]);
      }
    });

    if (this.moduleList[this.instance.module].i18n) {
      this.moduleList[this.instance.module].i18n.locale = this.$i18n.locale;
    } else {
      this.moduleList[this.instance.module].i18n = {
        locale: this.$i18n.locale,
      };
    }
  },
  async mounted() {
    // Helper function to iterate through global data
    // and set this.child props
    const globalDataToChild = (globalData) => {
      if (globalData) {
        Object.entries(globalData).forEach(([key, value]) => {
          if (key in this.child.$props) {
            this.child.$props[key] = value;
          }
        });
      }
    };

    // Sets default unwatch and helper function to set watch based on the global data id
    let unwatch = () => {};

    const globalWatch = (globalId) =>
      this.$watch(() => this.globalData(globalId)?.data, {
        handler: globalDataToChild,
        deep: true,
      });

    // Overwrite initial propdata if there's any global data
    // reference in it and sets a watcher for the global data
    const setGlobalData = async (data) => {
      unwatch();

      const [globalKey, { globalReference } = {}] =
        Object.entries(data).find(
          ([, value]) => isObject(value) && 'globalReference' in value,
        ) || [];

      let globalData = {};

      if (globalKey) {
        // Set global key as undefined so it always overwrites it from origin
        // allowing to handle not found globals
        globalData = { [globalKey]: undefined };

        if (globalReference) {
          try {
            // Overwrite default undefined global key
            // and load global data when selected
            ({ data: globalData } =
              (await this.$store.dispatch('loadGlobal', {
                endpoint: 'globals',
                id: globalReference,
              })) || {});
          } catch (error) {
            console.warn(error);
          }

          // Set global watcher by data value reference ID
          unwatch = globalWatch(globalReference);
        }

        // Directly mutate this.child props when triggered by watch
        if (this.child) {
          globalDataToChild({ ...data, ...globalData });
        }

        return { ...data, ...globalData };
      }

      // Directly mutate this.child props when triggered by watch
      if (this.child) {
        globalDataToChild(data);
      }

      return data;
    };

    this.$watch(() => this.instance.data, {
      handler: setGlobalData,
      deep: true,
    });

    const globalData = await setGlobalData(this.instance.data);

    const propsData = {
      ...globalData,
      instanceId: this.instanceId,
    };

    this.child = new (Vue.extend(this.moduleList[this.instance.module]))({
      store: this.$store,
      router: this.$router,
      propsData,
    });

    this.$store.commit('ADD_INSTANCE_THIS', this.child);

    const instanceWrapper = document.createElement('div');
    instanceWrapper.className = 'instance-wrapper';
    instanceWrapper.appendChild(this.child.$mount().$el);
    this.$el.appendChild(instanceWrapper);

    this.Toast = new Toast({
      customClass: {
        container: 'published-link',
      },
      position: 'top-end',
    });
  },
  beforeDestroy() {
    this.child.$destroy();
    this.$store.commit('REMOVE_INSTANCE_THIS', this.instanceId);
  },
  methods: {
    toggleButton({ clientY }) {
      const { height, top } =
        this.$refs['editable-instance'].getBoundingClientRect();

      if (clientY - top > height / 2) {
        this.showButton = 'bottom';
      } else {
        this.showButton = 'top';
      }
    },
    goToVisibility() {
      const groupPath = this.metadata.tabs[0].groups.findIndex(
        (item) => item.name === 'visibility',
      );

      this.setActiveInstanceExpandedGroup(`0-${groupPath}`);
      this.$store.dispatch('toggleActiveInstance', this.instanceId);
    },
    mathedPagesLimit(limit) {
      return this.pagesList.slice(0, Math.min(this.pagesList.length, limit));
    },
    toastPublishedPage(toastFeedback) {
      // TODO: Change svg to single icon component
      // which already exists and analyze this toast
      this.Toast.fire({
        showCloseButton: true,
        background: '#FFFFFF',
        html: `
        <style>
          .toast-checked {
            width: 100%;
            float: left;
            padding: 24px 33px 24px 0px;
            display: flex;
            align-items: center;
          }
          .toast-svg {
            padding-right: 16px;
            padding-left: 17px;
          }
          .swal2-close {
            position: absolute !important;
            top: 0;
            right: 0;
            width: 1.2em;
            height: 1.2em;
            transition: color 0.1s ease-out;
            border: none;
            background: transparent;
            color: #cccccc;
            font-family: serif;
            font-size: 40px;
            cursor: pointer;
            margin: 4px 4px 0 0;
          }
          .published-link .swal2-toast {
              padding: 0 0 10px 0 !important
          }
          </style>
          <input class="published-link__input" value="https://google.com.br" id="published-link" />
          <div class="toast-checked">
            <div class="toast-svg">
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0.6875C5.97676 0.6875 0.6875 5.97676 0.6875 12.5C0.6875 19.0232 5.97676 24.3125 12.5 24.3125C19.0232 24.3125 24.3125 19.0232 24.3125 12.5C24.3125 5.97676 19.0232 0.6875 12.5 0.6875ZM17.6021 8.64248L12.0491 16.3417C11.9715 16.45 11.8692 16.5383 11.7507 16.5992C11.6321 16.6601 11.5008 16.6918 11.3675 16.6918C11.2343 16.6918 11.1029 16.6601 10.9844 16.5992C10.8659 16.5383 10.7635 16.45 10.6859 16.3417L7.39795 11.7854C7.29775 11.6457 7.39795 11.4506 7.56934 11.4506H8.80596C9.0749 11.4506 9.33066 11.5798 9.48887 11.8013L11.3662 14.4063L15.5111 8.6583C15.6693 8.43945 15.9225 8.30762 16.194 8.30762H17.4307C17.6021 8.30762 17.7022 8.50273 17.6021 8.64248V8.64248Z" fill="#7CC970"/>
              </svg>
            </div>
            <div>
              ${toastFeedback}
            </div>
          </div>
        `,
      });
    },
    setVisibilityPageList() {
      this.enablePageList = !this.enablePageList;
    },
    setVisibilityCloneDropdown() {
      this.enableCloneDropdown = !this.enableCloneDropdown;
    },
    findPage(page) {
      const term = slugify(this.searchTerm, { lower: true, strict: true });
      const pageName = slugify(page.title, { lower: true, strict: true });
      return pageName.includes(term);
    },
    toogleEdition() {
      this.setActiveInstanceExpandedGroup(null);
      this.$store.dispatch('toggleActiveInstance', this.instanceId);
    },
    moveInstanceUp() {
      this.$store.dispatch('moveInstanceUp', this.instanceId);
    },
    moveInstanceDown() {
      this.$store.dispatch('moveInstanceDown', this.instanceId);
    },
    cloneInstance() {
      this.$store.dispatch('cloneInstance', this.instanceId);
    },
    cloneInstanceToAnotherPage(page) {
      const urlPath = this.mountUrlPath(page);
      this.$store
        .dispatch('cloneInstanceToAnotherPage', {
          instanceId: this.instanceId,
          urlPath,
        })
        .then(() => {
          const toastFeedback = `<p> Módulo copiado com sucesso! <br> Acesse a página <a href="/editor/${urlPath}" target="_blank" >aqui</a>. </p>`;

          this.setVisibilityCloneDropdown();
          this.toastPublishedPage(toastFeedback);
        })
        .catch(() => {
          const message = this.isUniqueInstance
            ? 'Apenas um módulo deste tipo é permitido na página.'
            : 'Este módulo não pode mais ser copiado.';
          const toastFeedback = `<p>${message}</p>`;

          this.setVisibilityCloneDropdown();
          this.toastPublishedPage(toastFeedback);
        });
    },
    mountUrlPath(page) {
      let urlPath = '';

      if (typeof page.language === 'undefined') {
        urlPath = `pt/${page.urlPath}`;
      } else {
        urlPath = `${page.language}/${page.urlPath}`;
      }
      return urlPath;
    },
    removeInstance() {
      const headingId = this.containerSettings.headingId?.split('-')[1];

      if (this.instanceId === headingId) {
        this.containerSettings.headingId = null;
      }

      this.$store.dispatch('removeInstance', this.instanceId);
    },
    ...mapActions([
      'setActiveInstanceExpandedGroup',
      'togglePresetListDisplay',
    ]),
  },
};
</script>

<style scoped lang="scss">
.editable-instance {
  position: relative;
  width: 100%;
  border: 0;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0.2;
    opacity: 0;
    transition: all 0.05s ease-in-out;
  }

  &::after {
    content: 'Editar';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: $lb-white;
    border-radius: 50px;
    width: 148px;
    height: 32px;
    line-height: 32px;
    color: $crm-blue-primary;
    font-size: $size-small;
    font-weight: $lb-medium;
    text-align: center;
    margin: auto;
    opacity: 0;
    transition: all 0.05s ease-in-out;
    box-shadow: 0 2px 5px fade-out($crm-blue-hover2, 0.5);
  }

  &:hover {
    &::before {
      background: rgba(70, 106, 240, 0.2);
      box-shadow: inset 0px 0px 0px 2px $crm-blue-primary;
      opacity: 1;
      z-index: 999999;
    }

    &::after {
      opacity: 1;
      z-index: 999999;
    }

    .editable-instance__actions {
      display: block;
    }
  }

  &--editing {
    &::before {
      box-shadow: inset 0px 0px 0px 2px $crm-blue-primary;
      opacity: 1;
      z-index: 9;
    }
  }

  &--modal {
    min-height: 470px;
    background-color: rgba($crm-blue-primary, 0.1);
  }

  .btn {
    position: absolute;
    left: 50%;
    z-index: 999999;

    &--top {
      top: 0;
      transform: translate(-50%, -50%);
    }

    &--bottom {
      bottom: 0;
      transform: translate(-50%, 50%);
    }
  }

  &__actions {
    display: none;
    z-index: 999999;
    position: absolute;
    right: 0;
    bottom: 100%;
    margin-right: -0px;
    box-shadow: 0px 0px 1px 0px black;
    border-radius: 3px 3px 0 0;

    button {
      padding: 6px 18px;

      &:hover {
        background: $crm-blue-hover1;
      }

      &:focus {
        outline: none;
      }

      &:disabled {
        opacity: 0.3;
      }
    }

    &--modal {
      width: 300px;

      .visibility {
        top: 20px;
      }
    }
  }
}

.clone-dropdown {
  width: 100%;
  background-color: $lb-white;
  position: absolute;

  button {
    padding: 16px 18px;
    text-align: initial;
    width: 100%;
    font-style: normal;
    font-weight: normal;
    font-size: $size-smaller;
    line-height: 14px;
    margin: 0;

    img {
      padding-left: 10px;
      padding-right: 10px;
    }

    img.arrow-up {
      transform: rotate(180deg);
    }
  }

  .show-page-list {
    background: rgba(196, 196, 196, 0.2) !important;
  }

  hr {
    padding: 0;
    margin: 0;
  }

  .search {
    position: relative;
    padding: 0 18px;

    i {
      position: absolute;
      right: 15px;
      top: 6px;
    }

    &__field {
      background: none;
      margin: 0;

      &::placeholder {
        color: #949494;
      }
    }
  }

  .list-pages {
    padding-top: 8px;
    max-width: 170px;

    li {
      padding: 8px 0 8px 0;
      font-size: $size-small;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;

      &:hover {
        color: $crm-blue-primary;
      }
    }
  }
}

.visibility {
  background: rgba(251, 188, 85, 1);
  padding: 14px 8px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  position: absolute;
  right: 20px;
  top: 60px;
  z-index: 999999;
  box-shadow: 4px 4px 25px 0px rgba(0, 0, 0, 0.25);
  outline: none;

  .icon--sm {
    width: 16px;
  }

  &:hover {
    background: rgba(251, 208, 105, 1) !important;
  }
}
</style>
